import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MediaLocalPreviewService } from '@services/media-local-preview/media-local-preview.service';
import { Store } from '@ngrx/store';
import { formatPatientName } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { videoUploadsActions } from '@shared/store/video-uploads/video-uploads.actions';

@Injectable({ providedIn: 'root' })
export class SequenceVideoUploadService {
  private readonly store = inject(Store);
  private readonly mediaLocalPreviewService = inject(MediaLocalPreviewService);
  private readonly translocoService = inject(TranslocoService);

  // Dispatches the video upload. The reducer seeds the sync-center store straight off this action,
  // then the effect runs the chunked upload. No-op when there are no videos. A unique id and a
  // preview object URL are created here (side effects) per file and passed in the action so the
  // reducer stays pure — the id keys the video in the store, so a new batch never overwrites the
  // sequence's existing sync-center entries (e.g. an interrupted upload).
  uploadVideos(clientCode: string, sequence: FlattenedSequence, videos: File[]): void {
    if (videos.length === 0) {
      return;
    }

    const sequenceId = sequence.sequenceInstance.id;

    this.store.dispatch(
      videoUploadsActions.uploadSequenceVideos({
        clientCode,
        sequenceId,
        // Same name rendering as the patient card (LASTNAME Firstname), "Anonyme" when there is no
        // patient — never the raw `fullName`, whose parts are in the reverse order.
        title: sequence.patientInstance
          ? formatPatientName(sequence.patientInstance)
          : this.translocoService.translate('Anonyme'),
        createdOn: sequence.sequenceInstance.createdOn,
        videos: videos.map((file) => {
          const videoId = crypto.randomUUID();
          return { videoId, file, previewUrl: this.mediaLocalPreviewService.register(videoId, file) };
        }),
      }),
    );
  }
}
